import i18n from '@/i18n';
import {
  ProjectEventTypeDetailsActions,
  ProjectEventTypeDetailsMutations,
  ProjectEventTypeDetailsGetters
} from './project-event-type-details.keys';
import { projectEventTypeDetailsService } from './project-event-type-details.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { TolMessagesActions } from '@/app/features/tol-messages/tol-messages.keys';

export const getDefaultState = () => {
  return {
    projectEventType: {},
    projectEventTypeAttachments: [],
    projectTools: [],
    isDetailsLoading: false,
    isLoadingAttachments: false,
    isSavingProjectEventType: false,
    projectEventTypeTechRatesSortOrder: {
      sortBy: 'rate_code',
      sortDirection: 'desc'
    },
    projectEventTypeToolsSortOrder: {
      sortBy: 'name',
      sortDirection: 'desc'
    },
    projectEventTypeSpecialRequirementsSortOrder: {
      sortBy: 'description',
      sortDirection: 'desc'
    },
    projectEventTypeValidationErrors: {},
    projectEventTypeTechRatesValidationErrors: {},
    projectEventTypeBillingValidationErrors: {},
    projectEventTypeToolsValidationErrors: {},
    projectEventTypeSpecialRequirementsValidationErrors: {}
  };
};

export const projectEventTypeDetailsStore = {
  state: getDefaultState(),
  getters: {
    [ProjectEventTypeDetailsGetters.ProjectEventType]: state => {
      return state.projectEventType;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeAttachments]: state =>
      state.projectEventTypeAttachments,
    [ProjectEventTypeDetailsGetters.ProjectTools]: state => state.projectTools,
    [ProjectEventTypeDetailsGetters.IsProjectEventTypeDetailsLoading]: state => {
      return state.isDetailsLoading;
    },
    [ProjectEventTypeDetailsGetters.IsProjectEventTypeAttachmentsLoading]: state => {
      return state.isLoadingAttachments;
    },
    [ProjectEventTypeDetailsGetters.IsSavingProjectEventType]: state => {
      return state.isSavingProjectEventType;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeTechRatesSortOrder]: state => {
      return state.projectEventTypeTechRatesSortOrder;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeToolsSortOrder]: state => {
      return state.projectEventTypeToolsSortOrder;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeSpecialRequirementsSortOrder]: state => {
      return state.projectEventTypeSpecialRequirementsSortOrder;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeValidationErrors]: state => {
      return state.projectEventTypeValidationErrors;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeTechRatesValidationErrors]: state => {
      return state.projectEventTypeTechRatesValidationErrors;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeBillingValidationErrors]: state => {
      return state.projectEventTypeBillingValidationErrors;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeToolsValidationErrors]: state => {
      return state.projectEventTypeToolsValidationErrors;
    },
    [ProjectEventTypeDetailsGetters.ProjectEventTypeSpecialRequirementsValidationErrors]: state => {
      return state.projectEventTypeSpecialRequirementsValidationErrors;
    }
  },
  mutations: {
    [ProjectEventTypeDetailsMutations.SetProjectEventType]: (state, project) => {
      state.projectEventType = project;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeDetailsLoading]: (
      state,
      isLoading = true
    ) => {
      state.isDetailsLoading = isLoading;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeAttachmentsLoading]: (
      state,
      isLoading = true
    ) => {
      state.isProjectAttachmentsLoading = isLoading;
    },
    [ProjectEventTypeDetailsMutations.SetProjectTools]: (state, tools) => {
      state.projectTools = tools;
    },
    [ProjectEventTypeDetailsMutations.FetchProjectEventTypeAttachmentsSuccess]: (
      state,
      attachments
    ) => {
      state.projectEventTypeAttachments = attachments;
    },
    [ProjectEventTypeDetailsMutations.SetIsSavingProjectEventType]: (state, isSaving) => {
      state.isSavingProjectEventType = isSaving;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeTechRatesSortOrder]: (
      state,
      sortOrder
    ) => {
      state.projectEventTypeTechRatesSortOrder = sortOrder;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeToolsSortOrder]: (state, sortOrder) => {
      state.projectEventTypeToolsSortOrder = sortOrder;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeSpecialRequirementsSortOrder]: (
      state,
      sortOrder
    ) => {
      state.projectEventTypeSpecialRequirementsSortOrder = sortOrder;
    },
    [ProjectEventTypeDetailsMutations.SortProjectEventTypeTechRates]: (state, sortOrder) => {
      state.projectEventType.techRates.sort((a, b) => {
        if (sortOrder.sortDirection === 'desc') {
          return a[sortOrder.sortBy] - b[sortOrder.sortBy];
        }
        return b[sortOrder.sortBy] - a[sortOrder.sortBy];
      });
    },
    [ProjectEventTypeDetailsMutations.SortProjectEventTypeTools]: (state, sortOrder) => {
      state.projectEventType.tools.sort((a, b) => {
        if (sortOrder.sortDirection === 'desc') {
          return a[sortOrder.sortBy].localeCompare(b[sortOrder.sortBy]);
        }
        return b[sortOrder.sortBy].localeCompare(a[sortOrder.sortBy]);
      });
    },
    [ProjectEventTypeDetailsMutations.SortProjectEventTypeSpecialRequirements]: (
      state,
      sortOrder
    ) => {
      state.projectEventType.special_requirements.sort((a, b) => {
        if (sortOrder.sortDirection === 'desc') {
          return a[sortOrder.sortBy].localeCompare(b[sortOrder.sortBy]);
        }
        return b[sortOrder.sortBy].localeCompare(a[sortOrder.sortBy]);
      });
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeValidationErrorsSuccess]: (
      state,
      errors
    ) => {
      state.projectEventTypeValidationErrors = errors;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeTechRatesValidationErrorsSuccess]: (
      state,
      errors
    ) => {
      state.projectEventTypeTechRatesValidationErrors = errors;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeBillingValidationErrorsSuccess]: (
      state,
      errors
    ) => {
      state.projectEventTypeBillingValidationErrors = errors;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeToolsValidationErrorsSuccess]: (
      state,
      errors
    ) => {
      state.projectEventTypeToolsValidationErrors = errors;
    },
    [ProjectEventTypeDetailsMutations.SetProjectEventTypeSpecialRequirementsValidationErrorsSuccess]: (
      state,
      errors
    ) => {
      state.projectEventTypeSpecialRequirementsValidationErrors = errors;
    },
    [ProjectEventTypeDetailsMutations.DeleteProjectEventTypeValidationErrorKeySuccess]: (
      state,
      key
    ) => {
      const updatedErrors = Object.assign({}, state.projectEventTypeValidationErrors);
      delete updatedErrors[key];
      state.projectEventTypeValidationErrors = updatedErrors;
    },
    [ProjectEventTypeDetailsMutations.DeleteProjectEventTypeTechRatesValidationErrorKeySuccess]: (
      state,
      key
    ) => {
      const updatedErrors = Object.assign({}, state.projectEventTypeTechRatesValidationErrors);
      delete updatedErrors[key];
      state.projectEventTypeTechRatesValidationErrors = updatedErrors;
    },
    [ProjectEventTypeDetailsMutations.DeleteProjectEventTypeBillingValidationErrorKeySuccess]: (
      state,
      key
    ) => {
      const updatedErrors = Object.assign({}, state.projectEventTypeBillingValidationErrors);
      delete updatedErrors[key];
      state.projectEventTypeBillingValidationErrors = updatedErrors;
    },
    [ProjectEventTypeDetailsMutations.DeleteProjectEventTypeToolsValidationErrorKeySuccess]: (
      state,
      key
    ) => {
      const updatedErrors = Object.assign({}, state.projectEventTypeToolsValidationErrors);
      delete updatedErrors[key];
      state.projectEventTypeToolsValidationErrors = updatedErrors;
    },
    [ProjectEventTypeDetailsMutations.DeleteProjectEventTypeSpecialRequirementsValidationErrorKeySuccess]: (
      state,
      key
    ) => {
      const updatedErrors = Object.assign(
        {},
        state.projectEventTypeSpecialRequirementsValidationErrors
      );
      delete updatedErrors[key];
      state.projectEventTypeSpecialRequirementsValidationErrors = updatedErrors;
    }
  },
  actions: {
    [ProjectEventTypeDetailsActions.FetchProjectEventType]: (
      { commit, dispatch },
      referenceNumber
    ) => {
      commit(ProjectEventTypeDetailsMutations.SetProjectEventTypeDetailsLoading);
      return projectEventTypeDetailsService
        .fetchProjectEventType(referenceNumber)
        .then(projectEventType => {
          commit(ProjectEventTypeDetailsMutations.SetProjectEventType, projectEventType);
          commit(ProjectEventTypeDetailsMutations.SetProjectEventTypeDetailsLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('projects.eventTypes.details.errors.fetchEventType')
          });
          commit(ProjectEventTypeDetailsMutations.SetProjectEventTypeDetailsLoading, false);
        });
    },
    [ProjectEventTypeDetailsActions.FetchProjectEventTypeAttachments]: (
      { state, commit },
      referenceNumber
    ) => {
      return projectEventTypeDetailsService
        .fetchProjectEventTypeAttachments(
          referenceNumber,
          state.projectEventTypeAttachmentsSortOrder
        )
        .then(attachments => {
          commit(
            ProjectEventTypeDetailsMutations.FetchProjectEventTypeAttachmentsSuccess,
            attachments
          );
        });
    },
    [ProjectEventTypeDetailsActions.FetchProjectTools]: ({ commit }, referenceNumber) => {
      return projectEventTypeDetailsService.fetchProjectTools(referenceNumber).then(tools => {
        commit(ProjectEventTypeDetailsMutations.SetProjectTools, tools);
      });
    },
    [ProjectEventTypeDetailsActions.SaveProjectEventType]: (
      { commit, dispatch },
      eventTypeData
    ) => {
      commit(ProjectEventTypeDetailsMutations.SetIsSavingProjectEventType, true);

      projectEventTypeDetailsService
        .updateProjectEventType(eventTypeData)
        .then(updatedEventType => {
          commit(ProjectEventTypeDetailsMutations.SetProjectEventType, updatedEventType);
          dispatch(TolMessagesActions.SetMessages, {
            text: i18n.t('projects.eventTypes.details.saveSuccess', { name: updatedEventType.name })
          });
        })
        .catch(error => {
          const errorItems = Object.keys(error.data);
          let items = [];
          for (let item of errorItems) {
            if (item !== 'statusCode') {
              items = items.concat(error.data[item]);
            }
          }
          dispatch(TolErrorsActions.SetError, { items });
        });

      commit(ProjectEventTypeDetailsMutations.SetIsSavingProjectEventType, false);
    },
    [ProjectEventTypeDetailsActions.SetProjectEventTypeTechRatesSortOrder]: (
      { commit },
      sortOrder
    ) => {
      commit(ProjectEventTypeDetailsMutations.SetProjectEventTypeTechRatesSortOrder, sortOrder);
      commit(ProjectEventTypeDetailsMutations.SortProjectEventTypeTechRates, sortOrder);
    },
    [ProjectEventTypeDetailsActions.SetProjectEventTypeToolsSortOrder]: ({ commit }, sortOrder) => {
      commit(ProjectEventTypeDetailsMutations.SetProjectEventTypeToolsSortOrder, sortOrder);
      commit(ProjectEventTypeDetailsMutations.SortProjectEventTypeTools, sortOrder);
    },
    [ProjectEventTypeDetailsActions.SetProjectEventTypeSpecialRequirementsSortOrder]: (
      { commit },
      sortOrder
    ) => {
      commit(
        ProjectEventTypeDetailsMutations.SetProjectEventTypeSpecialRequirementsSortOrder,
        sortOrder
      );
      commit(ProjectEventTypeDetailsMutations.SortProjectEventTypeSpecialRequirements, sortOrder);
    }
  }
};
